/**
 * Appointment Templates Page - Termin-Textvorlagen bearbeiten
 * 
 * Ermöglicht das Bearbeiten der Standardtexte für:
 * - Vor-Ort-Termine
 * - Telefontermine
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Calendar,
  Phone,
  Home,
  Save,
  Loader2,
  RefreshCw,
  Info,
  Mail,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Default templates
const DEFAULT_TEMPLATES = {
  onsite: {
    greeting: "vielen Dank für Ihr Interesse! Wir freuen uns sehr, Ihnen mitteilen zu dürfen, dass Ihr <strong>persönlicher</strong> Beratungstermin vor Ort erfolgreich vereinbart wurde. Wir möchten diesen hiermit bestätigen.",
    phone_note: "",
    closing: "Wir freuen uns auf das persönliche Gespräch mit Ihnen!"
  },
  phone: {
    greeting: "vielen Dank für Ihr Interesse! Wir freuen uns sehr, Ihnen mitteilen zu dürfen, dass Ihr <strong>telefonischer</strong> Beratungstermin erfolgreich vereinbart wurde. Wir möchten diesen hiermit bestätigen.",
    phone_note: "Bitte stellen Sie sicher, dass Sie zum Terminzeitpunkt telefonisch erreichbar sind.",
    closing: "Wir freuen uns auf das telefonische Gespräch mit Ihnen!"
  }
};

export default function AppointmentTemplatesPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  
  // Load templates
  useEffect(() => {
    loadTemplates();
  }, [token]);
  
  const loadTemplates = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/appointment-templates/`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.templates) {
          // Merge with defaults to ensure all fields exist
          setTemplates({
            onsite: { ...DEFAULT_TEMPLATES.onsite, ...data.templates.onsite },
            phone: { ...DEFAULT_TEMPLATES.phone, ...data.templates.phone }
          });
        }
      }
    } catch (err) {
      console.error("Error loading templates:", err);
    } finally {
      setLoading(false);
    }
  };
  
  // Save templates
  const saveTemplates = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/appointment-templates/`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ templates })
      });
      
      if (!response.ok) {
        throw new Error("Fehler beim Speichern");
      }
      
      toast.success("Vorlagen gespeichert");
    } catch (err) {
      toast.error("Fehler beim Speichern der Vorlagen");
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to defaults
  const resetToDefaults = (type) => {
    setTemplates({
      ...templates,
      [type]: { ...DEFAULT_TEMPLATES[type] }
    });
    toast.info(`${type === "onsite" ? "Vor-Ort-Termin" : "Telefontermin"} Vorlagen zurückgesetzt`);
  };
  
  // Update template field
  const updateTemplate = (type, field, value) => {
    setTemplates({
      ...templates,
      [type]: {
        ...templates[type],
        [field]: value
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="appointment-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Termin-Textvorlagen
          </h1>
          <p className="text-muted-foreground">
            Bearbeiten Sie die Standardtexte für Terminbestätigungen
          </p>
        </div>
        
        <Button onClick={saveTemplates} disabled={saving} data-testid="save-templates-btn">
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Speichern
        </Button>
      </div>
      
      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Hinweis zu HTML-Formatierung</p>
              <p>Sie können einfaches HTML verwenden: <code className="bg-blue-100 px-1 rounded">&lt;strong&gt;fett&lt;/strong&gt;</code> für <strong>fett</strong>, 
                <code className="bg-blue-100 px-1 rounded ml-1">&lt;em&gt;kursiv&lt;/em&gt;</code> für <em>kursiv</em>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different appointment types */}
      <Tabs defaultValue="onsite" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="onsite" className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            Vor-Ort-Termin
          </TabsTrigger>
          <TabsTrigger value="phone" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Telefontermin
          </TabsTrigger>
        </TabsList>
        
        {/* Vor-Ort-Termin */}
        <TabsContent value="onsite" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="h-5 w-5 text-blue-600" />
                    Vor-Ort-Termin Texte
                  </CardTitle>
                  <CardDescription>
                    Diese Texte werden in der E-Mail für Vor-Ort-Termine verwendet
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => resetToDefaults("onsite")}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Standard
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Greeting */}
              <div className="space-y-2">
                <Label htmlFor="onsite-greeting" className="text-base font-semibold">
                  Begrüßungstext
                </Label>
                <p className="text-sm text-muted-foreground">
                  Erscheint nach "Sehr geehrte/r [Name],"
                </p>
                <Textarea
                  id="onsite-greeting"
                  value={templates.onsite.greeting}
                  onChange={(e) => updateTemplate("onsite", "greeting", e.target.value)}
                  rows={4}
                  className="font-mono text-sm"
                  data-testid="onsite-greeting-input"
                />
              </div>
              
              {/* Closing */}
              <div className="space-y-2">
                <Label htmlFor="onsite-closing" className="text-base font-semibold">
                  Abschlusstext
                </Label>
                <p className="text-sm text-muted-foreground">
                  Erscheint am Ende der E-Mail vor der Signatur
                </p>
                <Textarea
                  id="onsite-closing"
                  value={templates.onsite.closing}
                  onChange={(e) => updateTemplate("onsite", "closing", e.target.value)}
                  rows={2}
                  className="font-mono text-sm"
                  data-testid="onsite-closing-input"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Telefontermin */}
        <TabsContent value="phone" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-amber-600" />
                    Telefontermin Texte
                  </CardTitle>
                  <CardDescription>
                    Diese Texte werden in der E-Mail für Telefontermine verwendet
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => resetToDefaults("phone")}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Standard
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Greeting */}
              <div className="space-y-2">
                <Label htmlFor="phone-greeting" className="text-base font-semibold">
                  Begrüßungstext
                </Label>
                <p className="text-sm text-muted-foreground">
                  Erscheint nach "Sehr geehrte/r [Name],"
                </p>
                <Textarea
                  id="phone-greeting"
                  value={templates.phone.greeting}
                  onChange={(e) => updateTemplate("phone", "greeting", e.target.value)}
                  rows={4}
                  className="font-mono text-sm"
                  data-testid="phone-greeting-input"
                />
              </div>
              
              {/* Phone Note */}
              <div className="space-y-2">
                <Label htmlFor="phone-note" className="text-base font-semibold">
                  Wichtiger Hinweis (Telefontermin)
                </Label>
                <p className="text-sm text-muted-foreground">
                  Erscheint als rot markierter Hinweis unter den Termindaten
                </p>
                <Textarea
                  id="phone-note"
                  value={templates.phone.phone_note}
                  onChange={(e) => updateTemplate("phone", "phone_note", e.target.value)}
                  rows={2}
                  className="font-mono text-sm"
                  placeholder="z.B. Bitte stellen Sie sicher, dass Sie telefonisch erreichbar sind..."
                  data-testid="phone-note-input"
                />
              </div>
              
              {/* Closing */}
              <div className="space-y-2">
                <Label htmlFor="phone-closing" className="text-base font-semibold">
                  Abschlusstext
                </Label>
                <p className="text-sm text-muted-foreground">
                  Erscheint am Ende der E-Mail vor der Signatur
                </p>
                <Textarea
                  id="phone-closing"
                  value={templates.phone.closing}
                  onChange={(e) => updateTemplate("phone", "closing", e.target.value)}
                  rows={2}
                  className="font-mono text-sm"
                  data-testid="phone-closing-input"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Vorschau
          </CardTitle>
          <CardDescription>
            So wird der Text in der E-Mail erscheinen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="preview-onsite">
            <TabsList className="mb-4">
              <TabsTrigger value="preview-onsite">Vor-Ort</TabsTrigger>
              <TabsTrigger value="preview-phone">Telefon</TabsTrigger>
            </TabsList>
            
            <TabsContent value="preview-onsite">
              <div className="bg-gray-50 p-6 rounded-lg border space-y-4">
                <p className="text-gray-700">Sehr geehrter Herr Mustermann,</p>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: templates.onsite.greeting }} />
                <div className="bg-blue-100 p-4 rounded-lg border-l-4 border-blue-500">
                  <p className="font-semibold text-blue-900">📅 Ihre Termindaten</p>
                  <p className="text-blue-800">Datum: Montag, 15.04.2025</p>
                  <p className="text-blue-800">Uhrzeit: 10:00 - 11:00 Uhr</p>
                  <p className="text-blue-800">Ort: Musterstraße 123, 12345 Berlin</p>
                </div>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: templates.onsite.closing }} />
              </div>
            </TabsContent>
            
            <TabsContent value="preview-phone">
              <div className="bg-gray-50 p-6 rounded-lg border space-y-4">
                <p className="text-gray-700">Sehr geehrter Herr Mustermann,</p>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: templates.phone.greeting }} />
                <div className="bg-amber-100 p-4 rounded-lg border-l-4 border-amber-500">
                  <p className="font-semibold text-amber-900">📞 Ihre Termindaten</p>
                  <p className="text-amber-800">Datum: Montag, 15.04.2025</p>
                  <p className="text-amber-800">Uhrzeit: 10:00 - 11:00 Uhr</p>
                  <p className="text-amber-800">Telefon: Wir rufen Sie an</p>
                </div>
                {templates.phone.phone_note && (
                  <div className="bg-red-100 p-3 rounded-lg border-l-4 border-red-500">
                    <p className="text-red-800">📞 <strong>Wichtig:</strong> {templates.phone.phone_note}</p>
                  </div>
                )}
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: templates.phone.closing }} />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
